import React, { useCallback, useEffect, useState } from "react";
import {
  Download,
  Loader2,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserCog,
  X,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * AdminUsers, Administração → Usuários internos (gestão pastoral/ministerial).
 * SEM login público nesta sprint. Cadastra Nome + E-mail + Role + Status (obrigatórios)
 * e Telefone + WhatsApp + Notas (opcionais via accordion).
 */

const ROLES = [
  { v: "admin", label: "Admin" },
  { v: "pastor", label: "Pastor" },
  { v: "lider", label: "Líder" },
  { v: "premium", label: "Premium" },
  { v: "user", label: "Usuário" },
];
const STATUSES = [
  { v: "active", label: "Ativo" },
  { v: "inactive", label: "Inativo" },
  { v: "blocked", label: "Bloqueado" },
];

const EMPTY = {
  name: "",
  email: "",
  role: "user",
  status: "active",
  phone: "",
  whatsapp: "",
  notes: "",
};

export default function AdminUsers() {
  const [data, setData] = useState({ total: 0, items: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: "all", status: "all", q: "" });
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id,...} = edit
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.role !== "all") params.set("role", filters.role);
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.q) params.set("q", filters.q);
    api
      .get(`/admin/app-users?${params}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ total: 0, items: [], stats: null }))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (u) => {
    if (!window.confirm(`Excluir ${u.name}? Esta ação não pode ser desfeita.`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/admin/app-users/${u.id}`);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (filters.role !== "all") params.set("role", filters.role);
    if (filters.status !== "all") params.set("status", filters.status);
    const token = localStorage.getItem("token") || "";
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    fetch(`${apiBase}/api/admin/app-users/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `app_users_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const stats = data.stats || { by_role: {}, by_status: {}, grand_total: 0 };

  return (
    <section
      data-testid="admin-users-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#C5A059] mb-1">
            <UserCog size={12} strokeWidth={1.6} />
            <span className="text-[0.6rem] uppercase tracking-[0.28em] font-medium">
              Administração
            </span>
          </div>
          <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36] tracking-[-0.01em]">
            Usuários internos
          </h2>
          <p className="text-[0.78rem] text-[#0B1E36]/55 mt-1">
            Gestão pastoral e ministerial · login público desativado nesta etapa.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="admin-users-new"
            onClick={() => setEditing({ ...EMPTY })}
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-white bg-[#0B1E36] hover:bg-[#172a47] font-medium px-4 py-2.5"
          >
            <Plus size={13} strokeWidth={1.7} />
            Novo usuário
          </button>
          <button
            data-testid="admin-users-export"
            onClick={exportCSV}
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] bg-[#C5A059] hover:bg-[#D4B273] font-medium px-4 py-2.5"
          >
            <Download size={13} strokeWidth={1.7} />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Stat label="Total" value={stats.grand_total} />
        {ROLES.map((r) => (
          <Stat key={r.v} label={r.label} value={stats.by_role[r.v] || 0} />
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-[#FAF8F4] border border-[#0B1E36]/8 p-3 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1E36]/40" />
          <input
            data-testid="admin-users-search"
            value={filters.q}
            placeholder="Buscar por nome, e-mail, telefone…"
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className="w-full bg-white border border-[#0B1E36]/15 focus:border-[#C5A059] outline-none pl-9 pr-3 py-2 text-sm text-[#0B1E36] placeholder:text-[#0B1E36]/35"
          />
        </div>
        <SelectChip
          label="Role"
          value={filters.role}
          onChange={(v) => setFilters((f) => ({ ...f, role: v }))}
          options={[{ v: "all", label: "Todas" }, ...ROLES]}
        />
        <SelectChip
          label="Status"
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={[{ v: "all", label: "Todos" }, ...STATUSES]}
        />
      </div>

      {/* Table */}
      <div className="border border-[#0B1E36]/10 overflow-x-auto">
        <table className="w-full text-sm text-[#0B1E36]">
          <thead className="bg-[#FAF8F4] text-[0.6rem] uppercase tracking-[0.22em] text-[#0B1E36]/55">
            <tr>
              <th className="text-left px-3 py-2.5">Nome</th>
              <th className="text-left px-3 py-2.5">E-mail</th>
              <th className="text-left px-3 py-2.5">Role</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-left px-3 py-2.5">Telefone</th>
              <th className="text-left px-3 py-2.5">Criado em</th>
              <th className="text-right px-3 py-2.5">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-10 text-[#0B1E36]/50">
                <Loader2 size={16} className="inline animate-spin mr-2" /> Carregando…
              </td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-[#0B1E36]/45 italic">
                Nenhum usuário cadastrado.
              </td></tr>
            ) : (
              data.items.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`admin-user-row-${u.id}`}
                  className="border-t border-[#0B1E36]/8 hover:bg-[#FAF8F4]/60"
                >
                  <td className="px-3 py-2.5">{u.name}</td>
                  <td className="px-3 py-2.5 text-[#0B1E36]/70">{u.email}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-[0.62rem] uppercase tracking-[0.2em] text-[#C5A059] font-medium">
                      {ROLES.find((r) => r.v === u.role)?.label || u.role}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[0.62rem] uppercase tracking-[0.2em] font-medium ${
                      u.status === "active" ? "text-emerald-700" :
                      u.status === "blocked" ? "text-rose-700" : "text-[#0B1E36]/40"
                    }`}>
                      {STATUSES.find((s) => s.v === u.status)?.label || u.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[#0B1E36]/65 text-[0.8rem] tabular-nums">
                    {u.phone || u.whatsapp || "·"}
                  </td>
                  <td className="px-3 py-2.5 text-[#0B1E36]/50 text-[0.78rem] tabular-nums">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "·"}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button
                      data-testid={`admin-user-edit-${u.id}`}
                      onClick={() => setEditing(u)}
                      className="text-[0.62rem] uppercase tracking-[0.18em] text-[#0B1E36]/65 hover:text-[#C5A059] border-b border-transparent hover:border-[#C5A059] pb-0.5 mr-3"
                    >
                      Editar
                    </button>
                    <button
                      data-testid={`admin-user-delete-${u.id}`}
                      disabled={busyId === u.id}
                      onClick={() => remove(u)}
                      className="text-[0.62rem] uppercase tracking-[0.18em] text-rose-700/80 hover:text-rose-700 inline-flex items-center gap-1"
                    >
                      <Trash2 size={11} strokeWidth={1.7} />
                      Excluir
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[0.62rem] uppercase tracking-[0.18em] text-[#0B1E36]/45 flex items-center gap-2">
        <ShieldCheck size={11} strokeWidth={1.6} className="text-emerald-700/80" />
        LGPD, dados internos, sem exposição pública
      </p>

      {editing && (
        <UserDrawer
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#0B1E36]/10 bg-[#FAF8F4] p-3">
      <span className="block text-[0.55rem] uppercase tracking-[0.22em] text-[#C5A059] mb-1">
        {label}
      </span>
      <p className="text-[1.4rem] text-[#0B1E36] tabular-nums font-light leading-none">{value}</p>
    </div>
  );
}

function SelectChip({ label, value, onChange, options }) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0B1E36]/55">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-[#0B1E36]/15 text-[#0B1E36] text-xs px-2.5 py-2 focus:border-[#C5A059] outline-none"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function UserDrawer({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [openAdv, setOpenAdv] = useState(
    !!(initial.phone || initial.whatsapp || initial.notes)
  );
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.name.trim() || form.name.trim().length < 2)
      return setErr("Nome deve ter ao menos 2 caracteres.");
    if (!form.email.trim()) return setErr("E-mail é obrigatório.");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        status: form.status,
        phone: form.phone?.trim() || null,
        whatsapp: form.whatsapp?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/admin/app-users/${initial.id}`, payload);
      } else {
        await api.post(`/admin/app-users`, payload);
      }
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-user-drawer"
      className="fixed inset-0 z-[80] bg-[#0B1E36]/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-xl w-full max-h-[92vh] overflow-y-auto border border-[#0B1E36]/15"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#0B1E36]/10">
          <h3 className="font-serif-display text-[1.3rem] text-[#0B1E36]">
            {isEdit ? "Editar usuário" : "Novo usuário"}
          </h3>
          <button
            onClick={onClose}
            data-testid="admin-user-drawer-close"
            className="text-[#0B1E36]/60 hover:text-[#0B1E36]"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nome" required>
              <input
                data-testid="admin-user-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className={inp}
              />
            </Field>
            <Field label="E-mail" required>
              <input
                data-testid="admin-user-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className={inp}
              />
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Role" required>
              <select
                data-testid="admin-user-role"
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
                className={inp}
              >
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Status" required>
              <select
                data-testid="admin-user-status"
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                className={inp}
              >
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </Field>
          </div>

          <button
            type="button"
            onClick={() => setOpenAdv((v) => !v)}
            data-testid="admin-user-toggle-advanced"
            className="text-[0.65rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 hover:text-[#C5A059]"
          >
            {openAdv ? "▼" : "▸"} Informações adicionais (opcional)
          </button>

          {openAdv && (
            <div className="border-t border-[#0B1E36]/10 pt-4 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Telefone">
                  <input
                    data-testid="admin-user-phone"
                    value={form.phone || ""}
                    placeholder="(81) 99999-9999"
                    onChange={(e) => set("phone", e.target.value)}
                    className={inp}
                  />
                </Field>
                <Field label="WhatsApp">
                  <input
                    data-testid="admin-user-whatsapp"
                    value={form.whatsapp || ""}
                    placeholder="(81) 99999-9999"
                    onChange={(e) => set("whatsapp", e.target.value)}
                    className={inp}
                  />
                </Field>
              </div>
              <Field label="Notas internas">
                <textarea
                  data-testid="admin-user-notes"
                  value={form.notes || ""}
                  rows={3}
                  onChange={(e) => set("notes", e.target.value)}
                  className={`${inp} min-h-[80px]`}
                />
              </Field>
            </div>
          )}

          {err && (
            <p data-testid="admin-user-error" className="text-[0.8rem] text-rose-700 bg-rose-50 px-3 py-2 border border-rose-200">
              {err}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/65 px-4 py-2.5"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="admin-user-save"
              disabled={busy}
              className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] bg-[#C5A059] hover:bg-[#D4B273] disabled:opacity-60 font-medium px-5 py-2.5"
            >
              {busy ? "Salvando…" : isEdit ? "Salvar" : "Criar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inp =
  "w-full border border-[#0B1E36]/15 focus:border-[#C5A059] outline-none px-3 py-2.5 text-[0.92rem] text-[#0B1E36] bg-white";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[0.6rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 font-medium mb-1.5">
        {label}{required && <span className="text-[#C5A059] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
